/**
 * StageBadge Component
 * 
 * Color-coded badge for learning stage display
 */

import { cn } from '@/lib/utils';

export type LearningStage = 'cognitive' | 'associative' | 'autonomous';

interface StageBadgeProps {
  stage: LearningStage | null;
  className?: string;
}

const STAGE_CONFIG = {
  cognitive: {
    label: 'Cognitive',
    description: 'Learning the basics',
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-300',
    icon: '📚',
  },
  associative: {
    label: 'Associative',
    description: 'Refining technique',
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-300',
    icon: '🎯',
  },
  autonomous: {
    label: 'Autonomous',
    description: 'Mastered skill',
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-300',
    icon: '⭐',
  },
} as const;

export function StageBadge({ stage, className }: StageBadgeProps) {
  if (!stage) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium',
          'bg-gray-100 text-gray-600 border border-gray-300',
          className
        )}
        aria-label="Learning stage not set"
      >
        Not Started
      </span>
    );
  }

  const config = STAGE_CONFIG[stage];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border',
        config.bg,
        config.text,
        config.border,
        className
      )}
      aria-label={`Learning stage: ${config.label} - ${config.description}`}
      title={config.description}
    >
      <span aria-hidden="true">{config.icon}</span>
      {config.label}
    </span>
  );
}

